import { formatDate, parseDate } from '@travelata-libs/utils';
import {
  DATA_LAYER_BANNER_PLACEHOLDER_ID,
  DATA_LAYER_DATE_FORMAT,
  DATA_LAYER_EXCLUSIVE_FILTER_TYPE,
  DATA_LAYER_GOOUT_BANNER_TYPE,
  DATA_LAYER_HOTEL_CLASS_STARS,
  DATA_LAYER_IS_WL,
  DATA_LAYER_NO_OIL_TAX,
  DATA_LAYER_TOP_HOTELS_LIMIT,
  DATA_LAYER_TOP_HOTELS_TYPE,
} from '../config';
import {
  DataLayerEventName,
  type BannerClickDataLayerEvent,
  type BannerShowDataLayerEvent,
  type CalendarDayClickDataLayerEvent,
  type DataLayerFilterInteraction,
  type DataLayerListItem,
  type DataLayerRangeItem,
  type DataLayerSearchCriteria,
  type DataLayerSearchClick,
  type FilterInteractionDataLayerEvent,
  type FiltersAppliedDataLayerEvent,
  type FiltersOpenDataLayerEvent,
  type FilterToggleDataLayerEvent,
  type SearchClickDataLayerEvent,
  type SearchCountDataLayerEvent,
  type SearchDataLayerEvent,
  type DataLayerVariableEvent,
  type GooutBannerContent,
  type SideBarFilterDataLayerEvent,
} from '../model/dataLayerEvents';
import type { DataLayerEvent } from '../model/types';

export function getFilterToggleEvent(checked: boolean, value: string): FilterToggleDataLayerEvent {
  return { event: checked ? DataLayerEventName.FilterOn : DataLayerEventName.FilterOff, value };
}

export function getSideBarFilterEvent(beach: string): SideBarFilterDataLayerEvent {
  return { event: DataLayerEventName.SideBarFilter, beach };
}

export function getFiltersOpenEvent(): FiltersOpenDataLayerEvent {
  return { event: DataLayerEventName.FiltersOpen, is_wl: DATA_LAYER_IS_WL };
}

export function getFilterInteractionEvent(
  interaction: DataLayerFilterInteraction,
  filterPage: string,
): FilterInteractionDataLayerEvent {
  return {
    filter_type: DATA_LAYER_EXCLUSIVE_FILTER_TYPE,
    filter_interaction: interaction,
    filter_page: filterPage,
    is_wl: DATA_LAYER_IS_WL,
    event: DataLayerEventName.FilterInteraction,
  };
}

export function getFiltersAppliedEvent(isExclusive: boolean, filterPage: string): FiltersAppliedDataLayerEvent {
  return {
    filter_travelata_exclusive_only_regular_flights: isExclusive ? 1 : 0,
    filter_page: filterPage,
    is_wl: DATA_LAYER_IS_WL,
    event: DataLayerEventName.FiltersApplied,
  };
}

export function getTopHotelIds(hotelIds: readonly number[], bannersStep: number): number[] {
  const topHotelIds: number[] = [];

  for (let index = 0; index < hotelIds.length && topHotelIds.length < DATA_LAYER_TOP_HOTELS_LIMIT; index += 1) {
    topHotelIds.push(hotelIds[index]);

    if ((index + 1) % bannersStep === 0) topHotelIds.push(DATA_LAYER_BANNER_PLACEHOLDER_ID);
  }

  return topHotelIds;
}

export function getSearchCountEvent(count: number): SearchCountDataLayerEvent {
  return { event: DataLayerEventName.SearchCount, searchCount: count };
}

export function getSearchEvent(hotelIds: readonly number[], bannersStep: number): SearchDataLayerEvent {
  return {
    event: DataLayerEventName.Search,
    search: { prop: [{ type: DATA_LAYER_TOP_HOTELS_TYPE, value: getTopHotelIds(hotelIds, bannersStep).join(',') }] },
  };
}

export function getSearchClickEvent({
  hotelIndex,
  hotelId,
  price,
  oilTax,
}: DataLayerSearchClick): SearchClickDataLayerEvent {
  return {
    event: DataLayerEventName.SearchClick,
    searchClick: [
      { value: hotelIndex, type: 'hotelIndex' },
      { value: hotelId, type: 'hid' },
      { value: price, type: 'price' },
      { value: oilTax ?? DATA_LAYER_NO_OIL_TAX, type: 'taxoil' },
    ],
  };
}

export function getCalendarDayClickEvent(hasPrice: boolean): CalendarDayClickDataLayerEvent {
  return hasPrice
    ? { event: DataLayerEventName.PriceCalendarClick, clickPriceCalendar: '' }
    : { event: DataLayerEventName.EmptyCalendarClick, clickEmptyCalendar: '' };
}

export function getVariableEvent<N extends DataLayerEventName, V>(name: N, value: V): DataLayerVariableEvent<N, V> {
  return { event: name, [name]: value } as DataLayerVariableEvent<N, V>;
}

function toRangeItems<V>(record: Readonly<Record<string, V>>): DataLayerRangeItem<V>[] {
  return Object.entries(record).map(([type, value]) => ({ value, type }));
}

function toListItems(values: readonly number[]): DataLayerListItem[] {
  return values.map((value) => ({ value }));
}

function toDataLayerDate(value: string): string {
  const date = parseDate(value);

  return date ? formatDate(date, DATA_LAYER_DATE_FORMAT) : value;
}

export function getHotelClassItems(hotelStars: readonly number[]): DataLayerRangeItem<0 | 1>[] {
  return DATA_LAYER_HOTEL_CLASS_STARS.map((stars) => ({
    value: !hotelStars.length || hotelStars.includes(stars) ? 1 : 0,
    type: `${stars}stars`,
  }));
}

export function getSearchCriteriaEvents(sid: string, criteria: DataLayerSearchCriteria): DataLayerEvent[] {
  const { from, to, daysFlexible } = criteria.dates;

  return [
    getVariableEvent(DataLayerEventName.Sid, sid),
    getVariableEvent(DataLayerEventName.CityFrom, criteria.cityFrom),
    getVariableEvent(DataLayerEventName.CountryTo, toListItems(criteria.countryIds)),
    getVariableEvent(DataLayerEventName.HotelClass, getHotelClassItems(criteria.hotelStars)),
    getVariableEvent(DataLayerEventName.TouristGroup, toRangeItems({ ...criteria.touristGroup })),
    getVariableEvent(DataLayerEventName.Night, toRangeItems({ ...criteria.nights })),
    getVariableEvent(
      DataLayerEventName.Date,
      toRangeItems({ from: toDataLayerDate(from), to: toDataLayerDate(to), daysFlexible }),
    ),
  ];
}

function getGooutBannerContent(value: string): GooutBannerContent {
  return { prop: [{ type: DATA_LAYER_GOOUT_BANNER_TYPE, value }] };
}

export function getBannerShowEvent(value: string): BannerShowDataLayerEvent {
  return { event: DataLayerEventName.InContent, inContent: getGooutBannerContent(value) };
}

export function getBannerClickEvent(value: string): BannerClickDataLayerEvent {
  return { event: DataLayerEventName.SelectContent, inContent: getGooutBannerContent(value) };
}
